import { useState, useEffect, useCallback, useRef } from 'react';
import { use_base_url } from './use_base_url';
import { sendClientErrorBeacon } from '../search_fetch';
import { debugLog, debugWarn } from '../debug';

// Name the ingest service verifies (previewmark.HeaderName). It gates the
// query and click endpoints independently, so both requests carry it.
export const PREVIEW_MARKER_HEADER = 'X-VO-Preview';

/**
 * Build the headers for a search or click request.
 *
 * The preview marker is attached only when the config carries one, and the
 * header is omitted entirely rather than sent empty: an empty value cannot
 * verify, so sending one would put a useless header on every ordinary
 * end-user request in the wild.
 *
 * The marker itself is server-minted and token-bound, so a page that supplies
 * a made-up one gains nothing -- verification fails and the request logs.
 *
 * @param {Object} config - Widget config; needs clientPublicToken, optionally previewMarker
 * @param {Object} [extra] - Additional headers to merge (the click request adds Content-Type)
 * @returns {Object} Headers for fetch
 */
export function buildRequestHeaders(config, extra = {}) {
  const headers = {
    'Authorization': config?.clientPublicToken,
    ...extra,
  };
  if (config?.previewMarker) {
    headers[PREVIEW_MARKER_HEADER] = config.previewMarker;
  }
  return headers;
}

// deriveBaseUrl recovers the widget's base URL from a full search endpoint URL
// so fire-and-forget helpers (which only receive a full URL) can address the
// beacon endpoint on the same origin/prefix.
function deriveBaseUrl(url) {
  if (typeof url !== 'string') return '';
  const marker = '/api/search';
  const i = url.indexOf(marker);
  return i >= 0 ? url.slice(0, i) : '';
}

/**
 * POST a click-tracking event and surface failures to the browser console.
 *
 * Click tracking is fire-and-forget at the call site, but transport errors and
 * non-OK responses must not be silent — that masked a dashboard "0 clicks" bug
 * for users with working POSTs that never landed in storage. Both rejection
 * and !response.ok branches log a single structured `console.error` with url,
 * status (when present), and any response body, so failures are visible in
 * devtools.
 */
export async function post_click_event(url, headers, body) {
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch (error) {
    console.error('[vo-search] click tracking failed', { url, error });
    sendClientErrorBeacon({
      baseUrl: deriveBaseUrl(url),
      token: headers?.Authorization,
      errorClass: 'click_tracking',
      status: 0,
      message: String(error?.message || error),
    });
    return;
  }
  if (!response.ok) {
    let respBody = '';
    try { respBody = await response.text(); } catch (_) { /* ignore */ }
    console.error('[vo-search] click tracking failed', {
      url,
      status: response.status,
      body: respBody,
    });
    sendClientErrorBeacon({
      baseUrl: deriveBaseUrl(url),
      token: headers?.Authorization,
      errorClass: 'click_tracking',
      status: response.status,
      message: `click tracking failed: ${response.status}`,
    });
  }
}

/**
 * Parse a Retry-After header value into whole seconds.
 *
 * Only the delta-seconds form is honoured; an HTTP-date or a missing header
 * yields 0, which callers read as "no window given".
 *
 * @param {string|null|undefined} value
 * @returns {number}
 */
function parseRetryAfterSeconds(value) {
  const seconds = Number.parseInt(value, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
}

/**
 * Classify a failed search request into a UI decision.
 *
 * AbortErrors are silent: a newer, more specific request superseded this one and
 * owns the loading/error state. Every other failure is surfaced regardless of
 * the request's specificity -- gating error handling on specificity previously
 * swallowed low-specificity failures, leaving the spinner running forever and
 * hiding the error from the user and from observability.
 *
 * A 429 is the one exception (#8363): it is back-pressure from a healthy
 * service, not a broken widget. It stays silent -- previous results remain on
 * screen, no error is set, and no client-error beacon fires -- and carries its
 * Retry-After window so autoplay can pause rather than type into the wall.
 *
 * @param {Error|{name?:string,message?:string,status?:number,retryAfterSeconds?:number}} err
 * @returns {{silent: boolean, rateLimited: boolean, message: string|null, errorClass: string|null, status: number, retryAfterSeconds: number}}
 */
export function handleRequestFailure(err) {
  if (err?.name === 'AbortError') {
    return { silent: true, rateLimited: false, message: null, errorClass: null, status: 0, retryAfterSeconds: 0 };
  }
  const status = Number.isFinite(err?.status) ? err.status : 0;
  if (status === 429) {
    const retryAfterSeconds = Number.isFinite(err?.retryAfterSeconds) ? err.retryAfterSeconds : 0;
    return { silent: true, rateLimited: true, message: null, errorClass: null, status, retryAfterSeconds };
  }
  return {
    silent: false,
    rateLimited: false,
    message: err?.message || 'Search request failed',
    errorClass: status > 0 ? 'query_http' : 'query_network',
    status,
    retryAfterSeconds: 0,
  };
}

/**
 * Instant search hook with progressive match grading.
 *
 * KEY FEATURES:
 * 1. PROGRESSIVE MATCH GRADING:
 *    - Tracks the highest specificity seen for results
 *    - Only renders results that increase specificity
 *    - Handles out-of-order responses gracefully
 *
 * 2. INSTANT EXECUTION:
 *    - Executes searches immediately on every keystroke
 *    - No debouncing - true instant search experience
 *
 * 3. REQUEST MANAGEMENT:
 *    - Optionally cancels out-of-range requests (disabled by default)
 *    - Only cancels requests with lower specificity than achieved
 *
 * EXAMPLE:
 * User types "instant search":
 * - "instant" results arrive: specificity = 7 → RENDER
 * - "insta" results arrive late: specificity = 5 → DISCARD (5 < 7)
 * - "instant s" results arrive: specificity = 9 → RENDER
 * - "instant se" results arrive: specificity = 10 → RENDER
 */
export const use_search = (config, initial_query, initial_page, enabled = true) => {
  const baseUrl = use_base_url();

  // State management
  const [query, set_query] = useState(initial_query || '');
  const [results, set_results] = useState({ hits: [], query: initial_query || '', hit_count: 0 });
  const [error, set_error] = useState(null);
  const [page, set_page] = useState(initial_page || 1);
  const [is_loading, set_is_loading] = useState(false);

  // Request management refs
  const abortControllerRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const pendingRequestsRef = useRef(new Map()); // Map: AbortController -> {query, sequence}

  // Progressive match tracking
  const progressiveMatchRef = useRef({
    highestSpecificity: 0,      // Highest specificity score seen
    bestMatchQuery: '',          // Query that produced highest specificity
    currentTargetQuery: '',      // What the user is currently typing
    lastQueryTime: 0            // Timestamp of last query change
  });

  // Default results cache
  const defaultResultsCacheRef = useRef({
    results: null,              // Cached default results
    configHash: null,           // Hash of config when cache was created
    isValid: false,             // Whether cache is valid
    previousConfig: null        // Previous config for comparison
  });

  // Track previous config for URL-related change detection
  const previousConfigRef = useRef(null);

  // Generate a simple hash of the config for cache invalidation
  const getConfigHash = useCallback((config) => {
    if (!config) return null;
    const relevantConfig = {
      clientPublicToken: config.clientPublicToken,
      resultsPerPage: config.resultsPerPage,
      showDefaultResults: config.showDefaultResults,
      resultLinkRootPath: config.resultLinkRootPath,
      refParam: config.refParam
    };
    return JSON.stringify(relevantConfig);
  }, []);

  /**
   * Calculate specificity score for a query match.
   * Higher scores mean more specific matches.
   * Updated to better handle fast typing scenarios.
   */
  const calculate_specificity = useCallback((searchQuery, targetQuery) => {
    if (!searchQuery || !targetQuery) return 0;

    const search = searchQuery.toLowerCase().trim();
    const target = targetQuery.toLowerCase().trim();

    // Exact match is highest specificity
    if (search === target) return 1000 + search.length;

    // If search is a prefix of target, give high score based on completeness
    // This is key for fast typing - partial matches should score well
    if (target.startsWith(search)) {
      const completeness = search.length / target.length;
      return 750 + completeness * 250;  // 750-1000 range
    }

    // If target is a prefix of search (over-typing), give decent score
    if (search.startsWith(target)) {
      const ratio = target.length / search.length;
      return 600 + ratio * 150;  // 600-750 range
    }

    // Calculate character overlap from start
    let overlap = 0;
    for (let i = 0; i < Math.min(search.length, target.length); i++) {
      if (search[i] === target[i]) {
        overlap++;
      } else {
        break;
      }
    }

    // Give meaningful scores to partial matches
    if (overlap > 0) {
      const ratio = overlap / Math.max(search.length, target.length);
      return 200 + ratio * 400;  // 200-600 range
    }

    return 0;
  }, []);

  /**
   * Core search function with progressive match filtering
   */
  const do_search = useCallback(async (search_query, page_num, search_config) => {
    const targetQuery = progressiveMatchRef.current.currentTargetQuery;
    debugLog(`[search] Starting search: query="${search_query}", target="${targetQuery}", page=${page_num}`);

    // Skip if no config
    if (!search_config || !search_config.clientPublicToken) {
      debugLog('[search] Skipping: Missing config or token');
      set_error('Search configuration not loaded');
      set_is_loading(false);
      return;
    }

    // Check for cached default results (empty query, page 1)
    const isDefaultResultsQuery = !search_query && page_num === 1 && search_config.showDefaultResults;
    const currentConfigHash = getConfigHash(search_config);

    if (isDefaultResultsQuery &&
        defaultResultsCacheRef.current.isValid &&
        defaultResultsCacheRef.current.configHash === currentConfigHash) {
      debugLog('[search] Using cached default results');
      set_results(defaultResultsCacheRef.current.results);
      set_is_loading(false);
      return;
    }

    // Skip empty search if showDefaultResults is false
    if (!search_query && !search_config.showDefaultResults) {
      debugLog('[search] Skipping empty search: showDefaultResults is false');
      progressiveMatchRef.current.highestSpecificity = 0;
      progressiveMatchRef.current.bestMatchQuery = '';
      set_results({ hits: [], query: '', hit_count: 0 });
      set_is_loading(false);
      return;
    }

    // Request management - cancel low-relevance requests
    const currentBest = progressiveMatchRef.current.highestSpecificity;
    if (currentBest > 0 && search_config.enableRequestCancellation) {
      // Cancel any pending requests that have lower specificity than current best
      const entries = Array.from(pendingRequestsRef.current.entries());
      const toCancel = entries.filter(([, info]) => {
        const spec = calculate_specificity(info.query, targetQuery);
        return spec < currentBest;
      });

      toCancel.forEach(([controller, info]) => {
        const spec = calculate_specificity(info.query, targetQuery);
        debugLog(`[search] Cancelling low-relevance request: "${info.query}" (specificity: ${spec} < ${currentBest})`);
        controller.abort();
        pendingRequestsRef.current.delete(controller);
      });
    }

    // Create abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const sequence = ++requestSequenceRef.current;
    pendingRequestsRef.current.set(controller, { query: search_query, sequence });

    // Set loading state
    set_is_loading(true);
    set_error(null);

    try {
      // Build request
      const form_data = new FormData();
      form_data.append('query', search_query);
      form_data.append('page', page_num);
      if (search_config.resultsPerPage !== undefined) {
        form_data.append('limit', search_config.resultsPerPage);
      }

      debugLog(`[search] Sending request #${sequence}: "${search_query}"`);

      const headers = buildRequestHeaders(search_config);

      // Send request
      const response = await fetch(`${baseUrl}/api/search/query`, {
        method: 'POST',
        headers,
        body: form_data,
        signal: controller.signal
      });

      // Remove from pending
      pendingRequestsRef.current.delete(controller);

      // Check if aborted
      if (controller.signal.aborted) {
        debugLog(`[search] Request #${sequence} aborted`);
        return;
      }

      // Handle errors
      if (!response.ok) {
        const httpError = new Error(`Search failed: ${response.statusText || response.status}`);
        httpError.status = response.status;
        httpError.retryAfterSeconds = parseRetryAfterSeconds(response.headers?.get?.('Retry-After'));
        throw httpError;
      }

      // Parse response
      const data = await response.json();

      // Calculate specificity for this response
      const currentTarget = progressiveMatchRef.current.currentTargetQuery;
      const specificity = calculate_specificity(search_query, currentTarget);
      const currentHighest = progressiveMatchRef.current.highestSpecificity;

      debugLog(`[search] Response #${sequence} for "${search_query}":`);
      debugLog(`  Target query: "${currentTarget}"`);
      debugLog(`  Specificity: ${specificity} (current highest: ${currentHighest})`);

      // Enhanced acceptance logic for fast typing scenarios
      let shouldAccept = false;
      let acceptanceReason = '';

      if (specificity >= currentHighest) {
        shouldAccept = true;
        acceptanceReason = 'improves specificity';
      } else if (search_query.length > progressiveMatchRef.current.bestMatchQuery.length) {
        // If this query is longer than the current best, and it's still a reasonable match
        // give it priority - this handles fast typing where longer queries come in after shorter ones
        const isReasonableMatch = specificity > 500; // Only accept if it's still a decent prefix match
        if (isReasonableMatch) {
          shouldAccept = true;
          acceptanceReason = 'longer query with reasonable match';
        }
      }

      if (shouldAccept) {
        debugLog(`[search] ACCEPTING response - ${acceptanceReason}`);

        // Update progressive match tracking
        progressiveMatchRef.current.highestSpecificity = Math.max(specificity, currentHighest);
        progressiveMatchRef.current.bestMatchQuery = search_query;

        // Process and set results
        const processed_results = {
          ...data,
          hits: data.hits?.map(hit => ({
            ...hit,
            url: build_result_url(hit, search_config),
            featured: !!hit.featured
          })) || [],
          query: search_query,
          hit_count: data.estimatedTotalHits || (data.hits ? data.hits.length : 0)
        };

        // Cache default results for future use
        if (isDefaultResultsQuery) {
          debugLog('[search] Caching default results');
          defaultResultsCacheRef.current = {
            results: processed_results,
            configHash: currentConfigHash,
            isValid: true,
            previousConfig: search_config
          };
        }

        set_results(processed_results);
        set_is_loading(false);
      } else {
        debugLog(`[search] DISCARDING response - lower specificity and not longer query`);
        // Don't update loading state if we're discarding - there might be better results coming
        const hasPending = pendingRequestsRef.current.size > 0;
        if (!hasPending) {
          set_is_loading(false);
        }
      }

    } catch (err) {
      pendingRequestsRef.current.delete(controller);

      const decision = handleRequestFailure(err);
      if (decision.rateLimited) {
        // Back-pressure, not breakage: keep the results already on screen and
        // stay quiet, but let the spinner go so the UI does not hang. The
        // signal lets the demo's autoplay pause for the window rather than
        // keep typing into it (#8364).
        debugLog(`[search] Request #${sequence} rate limited (retry after ${decision.retryAfterSeconds}s)`);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('vo-search-rate-limited', {
            detail: { retryAfterSeconds: decision.retryAfterSeconds },
          }));
        }
        if (pendingRequestsRef.current.size === 0) {
          set_is_loading(false);
        }
        return;
      }
      if (decision.silent) {
        debugLog(`[search] Request #${sequence} cancelled`);
        return;
      }

      // Every non-abort failure is surfaced and clears loading, no matter the
      // request's specificity. A swallowed low-specificity failure used to leave
      // the spinner running forever and hide the error. Loading only stays set
      // if a more specific request is still in flight to own the state.
      console.error(`[search] Search request #${sequence} failed:`, err);
      sendClientErrorBeacon({
        baseUrl,
        token: search_config.clientPublicToken,
        errorClass: decision.errorClass,
        status: decision.status,
        message: decision.message,
      });
      set_error(decision.message);
      if (pendingRequestsRef.current.size === 0) {
        set_is_loading(false);
      }
    }
  }, [baseUrl, calculate_specificity]);

  // Main search effect
  useEffect(() => {
    if (!enabled || !config?.clientPublicToken) return;

    // Update target query for progressive matching
    const previousTarget = progressiveMatchRef.current.currentTargetQuery;
    const now = Date.now();
    progressiveMatchRef.current.currentTargetQuery = query;
    progressiveMatchRef.current.lastQueryTime = now;

    // Reset progressive match if query changed significantly
    if (query !== previousTarget) {
      const isProgressive = query.startsWith(previousTarget) || previousTarget.startsWith(query);
      if (!isProgressive && query && previousTarget) {
        debugLog(`[search] Query changed significantly - resetting progressive match`);
        progressiveMatchRef.current.highestSpecificity = 0;
        progressiveMatchRef.current.bestMatchQuery = '';
      } else if (query.length < previousTarget.length) {
        // User deleted characters - reset specificity to allow shorter matches
        debugLog(`[search] Query shortened - resetting progressive match for backspacing`);
        progressiveMatchRef.current.highestSpecificity = 0;
        progressiveMatchRef.current.bestMatchQuery = '';
      }
    }

    // Execute search immediately
    if (query) {
      do_search(query, page, config);
    } else if (config.showDefaultResults) {
      // Reset for empty query
      progressiveMatchRef.current.highestSpecificity = 0;
      progressiveMatchRef.current.bestMatchQuery = '';
      do_search('', page, config);
    } else {
      // Clear results
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      progressiveMatchRef.current.highestSpecificity = 0;
      progressiveMatchRef.current.bestMatchQuery = '';
      set_results({ hits: [], query: '', hit_count: 0 });
      set_is_loading(false);
      set_error(null);
    }
  }, [query, page, config?.showDefaultResults, config?.clientPublicToken, config?.resultsPerPage, config?.resultLinkRootPath, config?.refParam, enabled, do_search]);

  // Separate effect for cache invalidation when config changes
  useEffect(() => {
    if (!config) return;

    const currentConfigHash = getConfigHash(config);
    const wasValid = defaultResultsCacheRef.current.isValid;
    const previousHash = defaultResultsCacheRef.current.configHash;
    const previousConfig = previousConfigRef.current;

    if (previousHash !== currentConfigHash) {
      debugLog('[search] Config changed - invalidating default results cache');
      defaultResultsCacheRef.current.isValid = false;

      // If we have an empty query and showDefaultResults=true, trigger a new search
      // to ensure URLs are updated with the new config
      if (!query && config.showDefaultResults && enabled) {
        debugLog('[search] Config changed with empty query - triggering new default search');
        // Use a timeout to avoid infinite loops
        setTimeout(() => {
          if (enabled && !query && config.showDefaultResults) {
            do_search('', page, config);
          }
        }, 0);
      }
    }

    // Update previous config for next comparison
    previousConfigRef.current = config;
  }, [config?.clientPublicToken, config?.resultsPerPage, config?.showDefaultResults, config?.resultLinkRootPath, config?.refParam, getConfigHash, query, enabled, page, do_search]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel all pending requests
      pendingRequestsRef.current.forEach((_, controller) => {
        controller.abort();
      });
      pendingRequestsRef.current.clear();
    };
  }, []);

  // Handle URL state updates
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.location?.href) return;

    const url = new URL(window.location.href);

    if (config?.pushState) {
      if (query) {
        url.searchParams.set('q', query);
      } else {
        url.searchParams.delete('q');
      }
      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }
    } else {
      url.searchParams.delete('q');
      url.searchParams.delete('page');
    }

    window.history.replaceState({ query, page }, '', url);
  }, [config?.pushState, query, page, enabled]);

  // Handle browser navigation
  useEffect(() => {
    if (!enabled || !config?.pushState || typeof window === 'undefined' || !window.location?.href) return;

    const handlePopState = () => {
      const url = new URL(window.location.href);
      const urlQuery = url.searchParams.get('q') || '';
      const urlPage = parseInt(url.searchParams.get('page') || '1', 10);
      set_query(urlQuery);
      set_page(urlPage);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [enabled, config?.pushState]);

  // Reset page to 1 when query changes
  const set_query_and_reset_page = useCallback((newQuery) => {
    set_query(newQuery);
    set_page(1);
  }, []);

  // Clear search function
  const clear_search = useCallback(() => {
    set_query('');
    set_page(1);
    progressiveMatchRef.current.highestSpecificity = 0;
    progressiveMatchRef.current.bestMatchQuery = '';

    debugLog('[search] clear_search: config.showDefaultResults=', config?.showDefaultResults);

    // If showDefaultResults, perform empty search, otherwise clear results
    if (config?.showDefaultResults) {
      debugLog('[search] clear_search: Performing empty search for default results');
      do_search('', 1, config);
    } else {
      debugLog('[search] clear_search: Clearing results (no default results)');
      set_results({ hits: [], query: '', hit_count: 0 });
    }
  }, [config, do_search]);

  // Just clear the query without performing a new search
  const clear_query = useCallback(() => {
    set_query('');
    set_page(1);
    progressiveMatchRef.current.highestSpecificity = 0;
  }, []);

  // Track click function for analytics
  // position: 1-indexed position within current page results
  const track_click = useCallback(async (result, position = 0) => {
    if (!config?.clientPublicToken || !result) return;

    // Calculate time to click from last search timestamp
    const timeToClick = progressiveMatchRef.current.lastQueryTime > 0
      ? Date.now() - progressiveMatchRef.current.lastQueryTime
      : 0;

    // Calculate absolute position (accounting for pagination)
    const resultsPerPage = config?.resultsPerPage ?? 10;
    const absolutePosition = position > 0
      ? position + ((page - 1) * resultsPerPage)
      : 0;

    const click_data = {
      query: query || '',
      doc_id: result.slug || '',
      doc_type: result.type === 'posts' ? 'post' : 'page',
      doc_title: result.title || '',
      doc_url: result.url || '',
      doc_slug: result.slug || '',
      position: absolutePosition,
      page: page,
      total_results: results?.hit_count || 0,
      time_to_click: timeToClick
    };

    const clickHeaders = buildRequestHeaders(config, { 'Content-Type': 'application/json' });

    await post_click_event(`${baseUrl}/api/search/query/click`, clickHeaders, click_data);
  }, [config, query, page, results, baseUrl]);

  // Return the public interface
  return {
    query,
    set_query: set_query_and_reset_page,
    results,
    error,
    page,
    set_page,
    clear_search,
    clear_query,
    track_click,
    is_loading,
    has_next_page: () => {
      const resultsPerPage = config?.resultsPerPage ?? 10;
      if (!config?.enablePagination || !results) return false;
      const total_results = results.hit_count;
      const current_position = (page * resultsPerPage);
      return current_position < total_results;
    },
    has_previous_page: () => {
      return config?.enablePagination && page > 1;
    },
    perform_search: do_search
  };
};

export function build_result_url(result, config) {
  if (!result?.slug) return '#';
  let url = '';
  const rootPath = config?.resultLinkRootPath || '/';
  url += ensure_trailing_slash(rootPath);
  url += result.slug;
  const refParam = config?.refParam;
  if (refParam) {
    // Defensive: normalize refParam in case it comes with "?" prefix (legacy format)
    // Legacy snippets used "?ref=search", new system expects "ref=search"
    const normalizedParam = refParam.startsWith('?') ? refParam.slice(1) : refParam;
    url += `?${normalizedParam}`;
  }
  return url;
}

function ensure_trailing_slash(v) {
  if (!v) return '/';
  return v.endsWith('/') ? v : v + '/';
}
